import React, { useState, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { 
  User, 
  FileText, 
  Edit, 
  Save, 
  Trash2,
  Upload,
  CheckCircle,
  Clock,
  MessageSquare,
  RotateCcw,
  Search,
  Filter,
  Grid,
  List,
  Eye,
  EyeOff,
  ArrowRight,
  ArrowDown,
  Zap,
  Database,
  Calendar,
  Users,
  Target,
  Activity,
  ZoomIn,
  ZoomOut,
  Maximize2,
  HelpCircle,
  Check,
  AlertTriangle,
  X
} from 'lucide-react';

const HistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  color: #1f2937;
  overflow: hidden;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem 1.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeaderTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ViewToggle = styled.div`
  display: flex;
  background: #e5e7eb;
  border-radius: 8px;
  padding: 0.25rem;
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#1f2937' : '#6b7280'};
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 2rem 1rem 1rem;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  flex-shrink: 0;
  position: sticky;
  top: 73px; /* Position below the internal header */
  z-index: 99;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s;
  
  &:hover {
    border-color: #9ca3af;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  option {
    padding: 0.5rem;
  }
`;

// Custom dropdown component for icon support
const CustomSelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  min-width: 160px; /* Ensure dropdown has minimum width */
`;

const CustomSelectButton = styled.button`
  width: 100%;
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    border-color: #9ca3af;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::after {
    content: '';
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #6b7280;
  }
`;

const CustomSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
`;

const CustomSelectOption = styled.div`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  
  &:hover {
    background: #f3f4f6;
  }
  
  &.selected {
    background: #eff6ff;
    color: #3b82f6;
    font-weight: 500;
  }
  
  svg {
    flex-shrink: 0;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const VisualizationArea = styled.div`
  flex: 1;
  position: relative;
  overflow: auto; /* Only this area scrolls */
  background: linear-gradient(45deg, #f8fafc 25%, transparent 25%), 
              linear-gradient(-45deg, #f8fafc 25%, transparent 25%), 
              linear-gradient(45deg, transparent 75%, #f8fafc 75%), 
              linear-gradient(-45deg, transparent 75%, #f8fafc 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const ScrollWrapper = styled.div`
  /* This wrapper provides the scrollable area */
  width: ${props => props.contentWidth * props.zoom}px;
  height: ${props => props.contentHeight * props.zoom}px;
  position: relative;
`;

const ZoomableContent = styled.div`
  transform: scale(${props => props.zoom});
  transform-origin: top left;
  transition: transform 0.3s ease;
  width: ${props => props.contentWidth}px;
  height: ${props => props.contentHeight}px;
  position: absolute;
  top: 0;
  left: 0;
`;

const NodesContainer = styled.div`
  /* Wrapper for all nodes to apply horizontal centering offset */
  position: relative;
  left: ${props => props.offsetX}px;
  width: 100%;
  height: 100%;
`;

const ZoomControls = styled.div`
  position: absolute;
  bottom: 2rem;
  right: ${props => props.detailPanelOpen ? 'calc(400px + 2rem)' : '2rem'}; /* Move left when detail panel is open */
  display: flex;
  gap: 0.5rem;
  background: white;
  border-radius: 8px;
  padding: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transition: right 0.3s ease; /* Smooth transition when panel opens/closes */
`;

const ZoomButton = styled.button`
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomLevel = styled.div`
  padding: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  min-width: 60px;
  text-align: center;
`;

const NodeContainer = styled.div`
  position: absolute;
  top: ${props => props.top}px;
  left: ${props => props.left}px;
  transform: translate(-50%, -50%);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: ${props => props.zIndex || 1};
`;

const Node = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: ${props => {
    // Field nodes use match status color if available
    if (props.type === 'field' && props.matchStatus) {
      return getMatchStatusBackground(props.matchStatus);
    }
    // Case node gets vibrant blue
    if (props.type === 'case') {
      return '#3b82f6'; // Vibrant blue
    }
    // User and action nodes get white background (for focused history view)
    if (props.type === 'user' || props.type === 'action') {
      return '#ffffff';
    }
    // Other node types use their default colors
    return props.type === 'field' ? '#9ca3af' : '#f3f4f6';
  }};
  border: 2px solid ${props => {
    // Field nodes use match status border color if available
    if (props.type === 'field' && props.matchStatus) {
      return getMatchStatusBorder(props.matchStatus);
    }
    // Case node gets darker blue border
    if (props.type === 'case') {
      return '#2563eb'; // Darker blue border
    }
    // User nodes get blue border
    if (props.type === 'user') {
      return '#3b82f6'; // Blue border
    }
    // Action nodes get purple border
    if (props.type === 'action') {
      return '#8b5cf6'; // Purple border
    }
    // Other node types use their default colors
    return props.type === 'field' ? '#6b7280' : '#6b7280';
  }};
  border-radius: 12px;
  min-width: ${props => props.type === 'case' ? '108px' :  // Case is 120% larger (90 * 1.2) - reduced from 150%
                         props.type === 'action' ? '160px' : 
                         props.focused ? '120px' : '90px'};
  padding: ${props => props.type === 'case' ? '1.2rem' :  // Proportionally reduced padding for case
                       props.focused ? '1rem' : '0.75rem'};
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  opacity: ${props => props.focused ? 1 : props.faded ? 0.3 : 1};
  transform: ${props => props.type === 'case' ? 'scale(1.2)' :  // Case is 120% scale (reduced from 150%)
                         props.focused ? 'scale(1.1)' : 'scale(1)'};
  
  &:hover {
    transform: ${props => props.type === 'case' ? 'scale(1.24)' : 'scale(1.05)'};
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }
  
  &.selected {
    transform: ${props => props.type === 'case' ? 'scale(1.28)' : 'scale(1.2)'};
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.3);
    z-index: 30;
  }
`;

const NodeIcon = styled.div`
  width: ${props => props.type === 'field' ? '36px' :  // Larger for fields
                     props.type === 'case' ? '40px' :   // Even larger for case
                     props.focused ? '32px' : '24px'};
  height: ${props => props.type === 'field' ? '36px' :  // Larger for fields
                      props.type === 'case' ? '40px' :   // Even larger for case
                      props.focused ? '32px' : '24px'};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    // White semi-transparent background for case and fields with match status
    if (props.type === 'case' || (props.type === 'field' && props.matchStatus)) {
      return 'rgba(255, 255, 255, 0.25)';
    }
    // Colored backgrounds for user and action nodes
    if (props.type === 'user') {
      return '#3b82f6'; // Blue
    }
    if (props.type === 'action') {
      return '#8b5cf6'; // Purple
    }
    return props.type === 'field' ? '#6b7280' : '#6b7280';
  }};
  color: ${props => {
    // White text for most nodes
    return 'white';
  }};
  border-radius: 50%;
  margin-bottom: ${props => props.focused ? '0.5rem' : '0.25rem'};
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  svg {
    width: ${props => props.type === 'field' ? '22px' :  // Larger icon for fields
                       props.type === 'case' ? '24px' :   // Larger icon for case
                       props.focused ? '16px' : '12px'};
    height: ${props => props.type === 'field' ? '22px' :  // Larger icon for fields
                        props.type === 'case' ? '24px' :   // Larger icon for case
                        props.focused ? '16px' : '12px'};
  }
`;

const NodeTitle = styled.div`
  font-size: ${props => props.focused ? '0.875rem' : '0.75rem'};
  font-weight: 600;
  color: ${props => {
    // White text for fields with match status and case nodes
    if ((props.nodeType === 'field' && props.matchStatus) || props.nodeType === 'case') {
      return 'white';
    }
    // Dark text for user and action nodes (they have white backgrounds)
    if (props.nodeType === 'user' || props.nodeType === 'action') {
      return '#1f2937';
    }
    return '#1f2937';
  }};
  text-align: center;
  margin-bottom: ${props => props.focused ? '0.25rem' : '0.125rem'};
  word-break: break-word;
  max-width: ${props => props.nodeType === 'action' ? '140px' : props.focused ? '100px' : '80px'};
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const NodeSubtitle = styled.div`
  font-size: ${props => props.focused ? '0.75rem' : '0.625rem'};
  color: ${props => {
    // White text for fields with match status and case nodes
    if ((props.nodeType === 'field' && props.matchStatus) || props.nodeType === 'case') {
      return 'rgba(255, 255, 255, 0.9)';
    }
    // Gray text for user and action nodes (they have white backgrounds)
    if (props.nodeType === 'user' || props.nodeType === 'action') {
      return '#6b7280';
    }
    return '#6b7280';
  }};
  text-align: center;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ConnectionLine = styled.div`
  position: absolute;
  height: 2px;
  background: linear-gradient(90deg, ${props => props.fromColor} 0%, ${props => props.toColor} 100%);
  transform-origin: left center;
  z-index: 1;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.focused ? 1 : props.faded ? 0.2 : 0.6};
  
  &::after {
    content: '';
    position: absolute;
    right: -4px;
    top: -3px;
    width: 0;
    height: 0;
    border-left: 6px solid ${props => props.toColor};
    border-top: 3px solid transparent;
    border-bottom: 3px solid transparent;
  }
`;

const DetailPanel = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 400px;
  height: 100%;
  background: white;
  border-left: 1px solid #e5e7eb;
  padding: 1.5rem;
  overflow-y: auto;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  z-index: 101;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const DetailTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  color: #6b7280;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 1rem;
`;

const DetailSectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e2e8f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TimelineIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color || '#3b82f6'};
  color: white;
  border-radius: 50%;
  flex-shrink: 0;
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const TimelineMeta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Mock data generator for node-based visualization
const REAL_PRODUCTION_FIELDS = [
  { id: 'field-1', name: 'Borrower Name', type: 'field', value: 'ABC Corp', changes: 2, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-2', name: 'Contract Date', type: 'field', value: '2024-01-15', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-3', name: 'Maturity Date', type: 'field', value: '2034-01-15', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-4', name: 'Original Loan Amount', type: 'field', value: '$2,500,000', changes: 5, matchStatus: 'mismatch', overrideStatus: 'user' },
  { id: 'field-5', name: 'Interest Rate Margin', type: 'field', value: '2.5%', changes: 3, matchStatus: 'maybe', overrideStatus: 'pending' },
  { id: 'field-6', name: 'Payment', type: 'field', value: '$15,234.56', changes: 2, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-7', name: 'Amortization', type: 'field', value: '360 months', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-8', name: 'Availability Balance', type: 'field', value: '$500,000', changes: 1, matchStatus: 'mismatch', overrideStatus: 'user' },
  { id: 'field-9', name: 'Balloon', type: 'field', value: 'None', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-10', name: 'Cap', type: 'field', value: '8.5%', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-11', name: 'Collateral Value', type: 'field', value: '$3,200,000', changes: 2, matchStatus: 'mismatch', overrideStatus: 'pending' },
  { id: 'field-12', name: 'Collateral Code', type: 'field', value: 'CRE-001', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-13', name: 'Collateral Lien Position', type: 'field', value: 'First', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-14', name: 'Risk Rating', type: 'field', value: 'Pass', changes: 1, matchStatus: 'maybe', overrideStatus: 'pending' },
  { id: 'field-15', name: 'Original Term', type: 'field', value: '120 months', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-16', name: 'Late Grace Days', type: 'field', value: '15', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-17', name: 'Min Rate', type: 'field', value: '3.5%', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-18', name: 'Max Rate', type: 'field', value: '9.5%', changes: 1, matchStatus: 'maybe', overrideStatus: 'pending' },
  { id: 'field-19', name: 'Debt Service Coverage', type: 'field', value: '1.25', changes: 2, matchStatus: 'mismatch', overrideStatus: 'user' },
  { id: 'field-20', name: 'Default Rate', type: 'field', value: '12%', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-21', name: 'Demand', type: 'field', value: 'No', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-22', name: 'Construction Conversion', type: 'field', value: 'N/A', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-23', name: 'Guarantor Name', type: 'field', value: 'John Smith', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-24', name: 'Guarantor Address', type: 'field', value: '123 Main St', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-25', name: 'Property Address', type: 'field', value: '456 Commerce Blvd', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-26', name: 'Property Type', type: 'field', value: 'Office Building', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-27', name: 'Property Value', type: 'field', value: '$3,200,000', changes: 3, matchStatus: 'maybe', overrideStatus: 'pending' },
  { id: 'field-28', name: 'Property LTV', type: 'field', value: '78%', changes: 2, matchStatus: 'mismatch', overrideStatus: 'user' },
  { id: 'field-29', name: 'Property Appraisal Date', type: 'field', value: '2024-01-01', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-30', name: 'Property Appraisal Value', type: 'field', value: '$3,200,000', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-31', name: 'Property Zoning', type: 'field', value: 'Commercial', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-32', name: 'Property Square Footage', type: 'field', value: '45,000 sq ft', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-33', name: 'Property Year Built', type: 'field', value: '2010', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-34', name: 'Property Bedrooms', type: 'field', value: 'N/A', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-35', name: 'Property Bathrooms', type: 'field', value: 'N/A', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-36', name: 'Property Lot Size', type: 'field', value: '2.5 acres', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-37', name: 'Property HOA', type: 'field', value: '$0', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-38', name: 'Property Taxes', type: 'field', value: '$45,000/year', changes: 1, matchStatus: 'maybe', overrideStatus: 'pending' },
  { id: 'field-39', name: 'Property Insurance', type: 'field', value: '$12,000/year', changes: 1, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-40', name: 'Property Flood Zone', type: 'field', value: 'Zone X', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-41', name: 'Property Flood Insurance', type: 'field', value: 'Not Required', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-42', name: 'Property Title Insurance', type: 'field', value: 'Required', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-43', name: 'Property Survey', type: 'field', value: 'Completed', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-44', name: 'Property Environmental', type: 'field', value: 'Phase I Complete', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-45', name: 'Property Phase I ESA', type: 'field', value: 'Pass', changes: 0, matchStatus: 'perfect', overrideStatus: 'system' },
  { id: 'field-46', name: 'Property Phase II ESA', type: 'field', value: 'Not Required', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-47', name: 'Property Asbestos', type: 'field', value: 'None Found', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-48', name: 'Property Lead Paint', type: 'field', value: 'N/A', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-49', name: 'Property Utilities', type: 'field', value: 'All Available', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
  { id: 'field-50', name: 'Property Access', type: 'field', value: 'Paved Road', changes: 0, matchStatus: 'no_data', overrideStatus: 'system' },
];

const SIMPLE_DEMO_FIELDS = [
  { id: 'field-1', name: 'Loan Amount', type: 'field', value: '$275,000', changes: 3 },
  { id: 'field-2', name: 'Borrower Name', type: 'field', value: 'John A. Doe', changes: 2 },
  { id: 'field-3', name: 'Interest Rate', type: 'field', value: '4.25%', changes: 1 },
  { id: 'field-4', name: 'Term Length', type: 'field', value: '30 years', changes: 0 },
  { id: 'field-5', name: 'Property Value', type: 'field', value: '$350,000', changes: 3 },
  { id: 'field-6', name: 'Down Payment', type: 'field', value: '$70,000', changes: 1 },
  { id: 'field-7', name: 'Credit Score', type: 'field', value: '750', changes: 1 },
  { id: 'field-8', name: 'Employment', type: 'field', value: 'Full-time', changes: 0 },
];

const generateNodeData = (useRealFields = false, loanId = '1048000010', activityCounts = {}) => ({
  fields: useRealFields ? REAL_PRODUCTION_FIELDS.map(field => ({
    ...field,
    changes: activityCounts[field.name] !== undefined ? activityCounts[field.name] : field.changes
  })) : SIMPLE_DEMO_FIELDS.map(field => ({
    ...field,
    changes: activityCounts[field.name] !== undefined ? activityCounts[field.name] : field.changes
  })),
  users: [
    { id: 'user-1', name: 'John Smith', type: 'user', actions: 8, role: 'Loan Officer' },
    { id: 'user-2', name: 'Sarah Johnson', type: 'user', actions: 5, role: 'Underwriter' },
    { id: 'user-3', name: 'Michael Chen', type: 'user', actions: 3, role: 'Processor' },
    { id: 'user-4', name: 'Lisa Davis', type: 'user', actions: 2, role: 'Manager' },
  ],
  cases: [
    { id: 'case-1', name: `Case #${loanId}`, type: 'case', status: 'Active', priority: 'High' },
  ],
  actions: useRealFields ? [
    // Original Loan Amount actions (field-4) - 5 changes
    { id: 'action-1', name: 'Field value updated', type: 'action', timestamp: '5 min ago', relatedField: 'field-4', relatedUser: 'user-1' },
    { id: 'action-2', name: 'Field value updated', type: 'action', timestamp: '15 min ago', relatedField: 'field-4', relatedUser: 'user-1' },
    { id: 'action-3', name: 'Field value updated', type: 'action', timestamp: '30 min ago', relatedField: 'field-4', relatedUser: 'user-1' },
    { id: 'action-4', name: 'Field value updated', type: 'action', timestamp: '1 hour ago', relatedField: 'field-4', relatedUser: 'user-2' },
    { id: 'action-5', name: 'Field value updated', type: 'action', timestamp: '2 hours ago', relatedField: 'field-4', relatedUser: 'user-1' },
    // Interest Rate Margin actions (field-5) - 3 changes
    { id: 'action-6', name: 'Field value updated', type: 'action', timestamp: '20 min ago', relatedField: 'field-5', relatedUser: 'user-2' },
    { id: 'action-7', name: 'Field value updated', type: 'action', timestamp: '45 min ago', relatedField: 'field-5', relatedUser: 'user-2' },
    { id: 'action-8', name: 'Field value updated', type: 'action', timestamp: '90 min ago', relatedField: 'field-5', relatedUser: 'user-1' },
    // Property Value actions (field-27) - 3 changes
    { id: 'action-9', name: 'Field value updated', type: 'action', timestamp: '10 min ago', relatedField: 'field-27', relatedUser: 'user-2' },
    { id: 'action-10', name: 'Field value updated', type: 'action', timestamp: '35 min ago', relatedField: 'field-27', relatedUser: 'user-2' },
    { id: 'action-11', name: 'Field value updated', type: 'action', timestamp: '3 hours ago', relatedField: 'field-27', relatedUser: 'user-3' },
    // Borrower Name actions (field-1) - 2 changes
    { id: 'action-12', name: 'Field value updated', type: 'action', timestamp: '25 min ago', relatedField: 'field-1', relatedUser: 'user-1' },
    { id: 'action-13', name: 'Field value updated', type: 'action', timestamp: '2 hours ago', relatedField: 'field-1', relatedUser: 'user-3' },
    // Other field updates
    { id: 'action-14', name: 'Field value updated', type: 'action', timestamp: '40 min ago', relatedField: 'field-11', relatedUser: 'user-3' },
    { id: 'action-15', name: 'Field value updated', type: 'action', timestamp: '50 min ago', relatedField: 'field-19', relatedUser: 'user-2' },
    { id: 'action-16', name: 'Field value updated', type: 'action', timestamp: '75 min ago', relatedField: 'field-28', relatedUser: 'user-2' },
    // Case-level actions
    { id: 'action-17', name: 'Case Review', type: 'action', timestamp: '4 hours ago', relatedField: null, relatedUser: 'user-4' },
    { id: 'action-18', name: 'Approval', type: 'action', timestamp: '5 hours ago', relatedField: null, relatedUser: 'user-4' },
  ] : [
    { id: 'action-1', name: 'Field Update', type: 'action', timestamp: '2 min ago', relatedField: 'field-1', relatedUser: 'user-1' },
    { id: 'action-2', name: 'Document Upload', type: 'action', timestamp: '15 min ago', relatedField: 'field-2', relatedUser: 'user-2' },
    { id: 'action-3', name: 'Status Change', type: 'action', timestamp: '1 hour ago', relatedField: null, relatedUser: 'user-3' },
    { id: 'action-4', name: 'Validation', type: 'action', timestamp: '2 hours ago', relatedField: 'field-3', relatedUser: 'user-1' },
    // Property Value field actions (3 field value updates by John Smith)
    { id: 'action-5', name: 'Field value updated', type: 'action', timestamp: '10 min ago', relatedField: 'field-5', relatedUser: 'user-1' },
    { id: 'action-5b', name: 'Field value updated', type: 'action', timestamp: '20 min ago', relatedField: 'field-5', relatedUser: 'user-1' },
    { id: 'action-5c', name: 'Field value updated', type: 'action', timestamp: '30 min ago', relatedField: 'field-5', relatedUser: 'user-1' },
    // Other actions by Lisa Davis (not related to Property Value)
    { id: 'action-6', name: 'Review', type: 'action', timestamp: '3 hours ago', relatedField: 'field-2', relatedUser: 'user-4' },
    { id: 'action-7', name: 'Approval', type: 'action', timestamp: '4 hours ago', relatedField: 'field-1', relatedUser: 'user-4' },
    { id: 'action-8', name: 'Case Review', type: 'action', timestamp: '5 hours ago', relatedField: null, relatedUser: 'user-4' },
  ]
});

const CONTENT_WIDTH = 1800;
const CONTENT_HEIGHT = 1400;
const HORIZONTAL_SCALE = 1.5; // Scale factor for oval shape (1.0 = circle, >1.0 = horizontal oval)
const STAGE_PADDING = 20; // Padding around all nodes to show boundaries

// Match status colors - VIBRANT primary colors with white text
const getMatchStatusBackground = (matchStatus) => {
  switch (matchStatus) {
    case 'perfect':
    case 'matched':
      return '#22c55e'; // Bright green
    case 'maybe':
      return '#f59e0b'; // Bright orange
    case 'mismatch':
      return '#ef4444'; // Bright red
    case 'no_data':
      return '#6ee7b7'; // Light green
    default:
      return '#9ca3af'; // Gray for unknown/default
  }
};

const getMatchStatusBorder = (matchStatus) => {
  switch (matchStatus) {
    case 'perfect':
    case 'matched':
      return '#16a34a'; // Darker green border
    case 'maybe':
      return '#d97706'; // Darker orange border
    case 'mismatch':
      return '#dc2626'; // Darker red border
    case 'no_data':
      return '#047857'; // Darker green border
    default:
      return '#6b7280'; // Gray border for unknown/default
  }
};

// Custom Select component with icon support
const CustomSelect = ({ value, onChange, options, placeholder = 'Select...' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <CustomSelectWrapper ref={wrapperRef}>
      <CustomSelectButton
        type="button"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedOption?.icon && <span style={{ display: 'flex', alignItems: 'center' }}>{selectedOption.icon}</span>}
        {selectedOption?.label || placeholder}
      </CustomSelectButton>
      {isOpen && (
        <CustomSelectDropdown>
          {options.map((option) => (
            <CustomSelectOption
              key={option.value}
              className={option.value === value ? 'selected' : ''}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.icon && <span style={{ display: 'flex', alignItems: 'center' }}>{option.icon}</span>}
              {option.label}
            </CustomSelectOption>
          ))}
        </CustomSelectDropdown>
      )}
    </CustomSelectWrapper>
  );
};

const NodeBasedHistory = ({ loanId = '1048000010', useRealFields = false, showBackButton = false, onBackClick }) => {
  const [viewMode, setViewMode] = useState('nodes'); // 'nodes' or 'timeline'
  const [selectedNode, setSelectedNode] = useState(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    user: 'all',
    timeframe: 'all',
    matchStatus: 'all', // perfect, mismatch, maybe, notfound
    overrideStatus: 'all' // system, user, pending
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [zoom, setZoom] = useState('auto'); // 'auto', or a number like 0.5, 1, 1.5
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [fieldHistory, setFieldHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [dynamicNodes, setDynamicNodes] = useState([]); // Nodes created from field history
  const [activityCounts, setActivityCounts] = useState({}); // Real activity counts from database

  // Fetch activity summary on mount to get real change counts
  useEffect(() => {
    const fetchActivitySummary = async () => {
      try {
        const url = `${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'}/api/activity/summary/${loanId}`;
        console.log('Fetching activity summary:', url);
        
        const response = await fetch(url, {
          headers: window.Cypress ? {
            'X-Test-Mode': 'true',
            'X-Test-DB-Name': 'ocr_matching_test',
            'X-Test-User': 'gary'
          } : {}
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Activity summary data:', data);
          const counts = {};
          data.forEach(item => {
            counts[item.field_name] = item.changes_count;
          });
          setActivityCounts(counts);
        } else {
          console.error('Failed to fetch activity summary:', response.status);
        }
      } catch (error) {
        console.error('Error fetching activity summary:', error);
      }
    };

    fetchActivitySummary();
  }, [loanId]);

  const nodeData = useMemo(() => generateNodeData(useRealFields, loanId, activityCounts), [useRealFields, loanId, activityCounts]);
  
  // Calculate filtered nodes and their count
  const filteredNodes = useMemo(() => {
    // Only show fields and case in default view - users appear only when a node is selected
    const allNodes = [...nodeData.fields, ...nodeData.cases];
    
    return allNodes.filter(node => {
      // ALWAYS show cases - only filter fields
      if (node.type === 'case') {
        return true;
      }
      
      // Apply search query filter (only to fields)
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        const nameMatch = node.name?.toLowerCase().includes(query);
        const valueMatch = node.value?.toLowerCase().includes(query);
        
        if (!nameMatch && !valueMatch) {
          return false;
        }
      }
      
      // Apply match status filter (only for fields)
      if (filters.matchStatus !== 'all' && node.type === 'field' && node.matchStatus && node.matchStatus !== filters.matchStatus) {
        return false;
      }
      
      // Apply override status filter (only for fields)
      if (filters.overrideStatus !== 'all' && node.type === 'field' && node.overrideStatus && node.overrideStatus !== filters.overrideStatus) {
        return false;
      }
      
      return true;
    });
  }, [nodeData, searchQuery, filters]);
  
  const filteredNodeCount = filteredNodes.length;
  const [focusedNodeData, setFocusedNodeData] = useState(null);
  const [relatedNodeIds, setRelatedNodeIds] = useState(new Set());
  const visualizationRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  // Calculate dynamic content dimensions based on visible nodes
  const contentDimensions = useMemo(() => {
    const visibleFields = filteredNodes.filter(n => n.type === 'field');
    const fieldCount = visibleFields.length;
    
    // Dynamic sizing based on visible field count
    let numLevels = 1;
    if (fieldCount > 40) numLevels = 3;
    else if (fieldCount > 20) numLevels = 2;
    else if (fieldCount > 12) numLevels = 2;
    else numLevels = 1;
    
    // Adjust base radius based on field count - use same logic as filtered positions
    let baseRadius = 280;
    const hasActiveFilters = filteredNodes.length < nodeData.fields.length;
    
    if (hasActiveFilters) {
      // When filters are active, use smaller radius for better focus
      if (fieldCount <= 3) baseRadius = 150;
      else if (fieldCount <= 5) baseRadius = 180;
      else if (fieldCount <= 10) baseRadius = 220;
      else if (fieldCount <= 15) baseRadius = 250;
    } else {
      // Default view
    if (fieldCount <= 5) baseRadius = 200;
    else if (fieldCount <= 10) baseRadius = 240;
    }
    
    const radiusIncrement = 150;
    const maxRadius = baseRadius + ((numLevels - 1) * radiusIncrement);
    
    // Calculate actual positions to determine content dimensions
    let centerX = 900 + STAGE_PADDING;
    let centerY = 650 + STAGE_PADDING;
    
    // Adjust center positions for filtered views - same logic as filtered positions
    if (hasActiveFilters) {
      // Adjust centerX - position more to the left for smaller filtered sets
      if (fieldCount <= 5) centerX = 600 + STAGE_PADDING;
      else if (fieldCount <= 10) centerX = 700 + STAGE_PADDING;
      else if (fieldCount <= 15) centerX = 800 + STAGE_PADDING;
      
      // Adjust centerY - position higher for smaller filtered sets
      if (fieldCount <= 3) centerY = 300 + STAGE_PADDING;
      else if (fieldCount <= 5) centerY = 350 + STAGE_PADDING;
      else if (fieldCount <= 10) centerY = 450 + STAGE_PADDING;
      else if (fieldCount <= 15) centerY = 550 + STAGE_PADDING;
    }
    
    // Users are positioned at: centerX - (maxRadius * HORIZONTAL_SCALE + 250)
    const maxFieldRadius = maxRadius * HORIZONTAL_SCALE;
    const userStartX = centerX - (maxFieldRadius + 250);
    
    // Fields extend to: centerX + maxFieldRadius
    const fieldsEndX = centerX + maxFieldRadius;
    
    // Calculate required dimensions with padding
    // Width: need to include from leftmost user to rightmost field + node widths
    // We want some padding before the users (start near 0 or STAGE_PADDING)
    // So total width = fieldsEndX + right padding
    const width = Math.max(
      fieldsEndX + 300, // Right side: rightmost field + node width + padding
      (centerX + maxFieldRadius) + 300 // Ensure we have enough width for the rightmost content
    );
    
    // Height: adjust based on centerY and maxRadius
    const height = Math.max(
      (centerY + maxRadius + 200 + STAGE_PADDING), // Bottom: center + radius + padding
      maxRadius * 2 + 400 + (STAGE_PADDING * 2) // Minimum height
    );
    
    return { width, height };
  }, [filteredNodes]);

  // Reset zoom to auto-fit and recenter whenever filters change
  useEffect(() => {
    setZoom('auto'); // Auto-fit to the filtered nodes
    
    // Scroll to center the filtered nodes in the visualization area
    if (visualizationRef.current) {
      setTimeout(() => {
        const container = visualizationRef.current;
        // Calculate center position
        const centerX = (container.scrollWidth - container.clientWidth) / 2;
        const centerY = (container.scrollHeight - container.clientHeight) / 2;
        container.scrollTo({
          left: Math.max(0, centerX),
          top: Math.max(0, centerY), // Center vertically
          behavior: 'smooth'
        });
      }, 100); // Slightly longer delay to allow zoom calculation
    }
  }, [searchQuery, filters.matchStatus, filters.overrideStatus]);

  // Calculate auto-zoom to fit everything in viewport
  useEffect(() => {
    if (zoom === 'auto' && visualizationRef.current) {
      const container = visualizationRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      // If a node is selected (focus mode), use 80% zoom
      if (selectedNode) {
        setZoomLevel(0.8);
        return;
      }
      
      // Simple approach: fit the actual content dimensions to the available viewport
      // contentDimensions already contains the correct width/height of our content
      const zoomX = containerWidth / contentDimensions.width;
      const zoomY = containerHeight / contentDimensions.height;
      
      // Use the smaller zoom to ensure everything fits without scrolling
      const autoZoom = Math.min(zoomX, zoomY, 3); // Max 300%
      
      // Add 10% to default zoom for better visibility
      setZoomLevel(Math.min(autoZoom + 0.1, 3));
    }
  }, [zoom, filteredNodes, selectedNode, contentDimensions]);

  // Center content on initial mount - run after zoom is calculated
  useEffect(() => {
    if (visualizationRef.current && zoomLevel > 0 && !selectedNode) {
      // Use longer delay on mount to ensure everything is rendered and zoom is calculated
      setTimeout(() => {
        const container = visualizationRef.current;
        if (!container) return;
        
        const centerX = (container.scrollWidth - container.clientWidth) / 2;
        
        console.log('🎯 Initial centering on mount:', {
          scrollWidth: container.scrollWidth,
          clientWidth: container.clientWidth,
          scrollHeight: container.scrollHeight,
          clientHeight: container.clientHeight,
          centerX: Math.max(0, centerX),
          scrollTop: 0,
          zoomLevel
        });
        
        container.scrollTo({
          left: Math.max(0, centerX),
          top: 0, // Scroll to top
          behavior: 'auto' // Instant on mount, no smooth scroll
        });
      }, 800); // Even longer delay for initial mount to ensure zoom is calculated
    }
  }, [zoomLevel]); // Run when zoom level is set

  // Center content when in auto zoom mode (no node selected)
  useEffect(() => {
    if (visualizationRef.current && zoom === 'auto' && !selectedNode && zoomLevel > 0) {
      setTimeout(() => {
        const container = visualizationRef.current;
        if (!container) return;
        
        // Center horizontally, scroll to top
        const centerX = (container.scrollWidth - container.clientWidth) / 2;
        
        console.log('🎯 Auto-centering:', {
          scrollWidth: container.scrollWidth,
          clientWidth: container.clientWidth,
          scrollHeight: container.scrollHeight,
          clientHeight: container.clientHeight,
          centerX: Math.max(0, centerX),
          scrollTop: 0,
          zoomLevel,
          contentDimensions
        });
        
        container.scrollTo({
          left: Math.max(0, centerX),
          top: 0, // Scroll to top
          behavior: 'smooth'
        });
      }, 250);
    }
  }, [zoom, zoomLevel, selectedNode, contentDimensions]);

  // Middle-click pan and scroll wheel zoom
  useEffect(() => {
    const container = visualizationRef.current;
    if (!container) return;

    // Handle mouse wheel for zooming
    const handleWheel = (e) => {
      if (e.ctrlKey) return; // Let browser handle Ctrl+Wheel normally
      
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.2, Math.min(3, zoomLevel + delta));
      setZoom('manual');
      setZoomLevel(newZoom);
    };

    // Handle middle mouse button for panning
    const handleMouseDown = (e) => {
      if (e.button === 1) { // Middle mouse button
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: e.clientX + container.scrollLeft, y: e.clientY + container.scrollTop });
        container.style.cursor = 'grabbing';
      }
    };

    const handleMouseMove = (e) => {
      if (!isPanning) return;
      e.preventDefault();
      const dx = panStart.x - e.clientX;
      const dy = panStart.y - e.clientY;
      container.scrollLeft = dx;
      container.scrollTop = dy;
    };

    const handleMouseUp = (e) => {
      if (e.button === 1) { // Middle mouse button
        setIsPanning(false);
        container.style.cursor = 'default';
      }
    };

    // Prevent context menu on middle click
    const handleContextMenu = (e) => {
      if (e.button === 1) {
        e.preventDefault();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('contextmenu', handleContextMenu);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('mousedown', handleMouseDown);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isPanning, panStart, zoomLevel]);

  // Keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + F to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Escape to clear search
      if (e.key === 'Escape' && searchQuery) {
        setSearchQuery('');
        searchInputRef.current?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchQuery]);

  const handleZoomIn = () => {
    setZoom('manual');
    setZoomLevel(prev => Math.min(prev + 0.1, 3)); // Max 300%
  };

  const handleZoomOut = () => {
    setZoom('manual');
    setZoomLevel(prev => Math.max(prev - 0.1, 0.2)); // Min 20%
  };

  const handleZoomReset = () => {
    setZoom('auto');
    
    // Scroll to center when auto-fitting
    if (visualizationRef.current) {
      setTimeout(() => {
        const container = visualizationRef.current;
        const centerX = (container.scrollWidth - container.clientWidth) / 2;
        const centerY = (container.scrollHeight - container.clientHeight) / 2;
        container.scrollTo({
          left: Math.max(0, centerX),
          top: Math.max(0, centerY), // Center vertically
          behavior: 'smooth'
        });
      }, 100); // Longer delay to allow zoom calculation
    }
  };

  // Memoize filtered positions calculation
  const filteredNodePositions = useMemo(() => {
    if (!filteredNodes || filteredNodes.length === 0) return null;
    
    // Function to calculate positions based on filtered nodes
    const getFilteredPositions = (filteredNodes) => {
    const positions = {};
    
    // Adjust centerX for filtered views - use smaller X for fewer nodes
    const fieldCountPreCalc = filteredNodes.filter(n => n.type === 'field').length;
    let centerX = 900 + STAGE_PADDING;
    
    // Position more to the left for smaller filtered sets
    if (fieldCountPreCalc <= 5) centerX = 600 + STAGE_PADDING;
    else if (fieldCountPreCalc <= 10) centerX = 700 + STAGE_PADDING;
    else if (fieldCountPreCalc <= 15) centerX = 800 + STAGE_PADDING;
    
    // Separate by type (users not shown in default view)
    const filteredFields = filteredNodes.filter(n => n.type === 'field');
    const filteredCases = filteredNodes.filter(n => n.type === 'case');
    
    // If no filters active, return null to use default layout
    const hasActiveFilters = searchQuery.trim() !== '' || filters.matchStatus !== 'all' || filters.overrideStatus !== 'all';
    if (!hasActiveFilters) {
      return null; // Use default layout
    }
    
    const fieldCount = filteredFields.length;
    
    // For filtered views, position higher up based on the number of nodes
    let centerY = 650 + STAGE_PADDING;
    
    // Adjust centerY based on field count - fewer fields = position higher
    if (fieldCount <= 3) centerY = 300 + STAGE_PADDING;
    else if (fieldCount <= 5) centerY = 350 + STAGE_PADDING;
    else if (fieldCount <= 10) centerY = 450 + STAGE_PADDING;
    else if (fieldCount <= 15) centerY = 550 + STAGE_PADDING;
    
    // Dynamic radius and levels based on filtered field count
    let numLevels = 1;
    if (fieldCount > 40) numLevels = 3;
    else if (fieldCount > 20) numLevels = 2;
    else if (fieldCount > 12) numLevels = 2;
    else numLevels = 1;
    
    // Adjust base radius based on field count - fewer fields = smaller radius for better focus
    let baseRadius = 280;
    if (fieldCount <= 3) baseRadius = 150;       // Very small filter, tight circle
    else if (fieldCount <= 5) baseRadius = 180;  // Small filter
    else if (fieldCount <= 10) baseRadius = 220; // Medium filter
    else if (fieldCount <= 15) baseRadius = 250; // Default
    
    const radiusIncrement = 150;
    const maxFieldRadius = baseRadius + ((numLevels - 1) * radiusIncrement);
    
    // Position case in center
    filteredCases.forEach(caseNode => {
      positions[caseNode.id] = { x: centerX, y: centerY };
    });
    
    // Position fields in concentric circles
    if (numLevels === 1) {
      // Single circle (or oval with horizontal scale)
      filteredFields.forEach((field, index) => {
        const angle = (index / fieldCount) * 2 * Math.PI;
        positions[field.id] = {
          x: centerX + baseRadius * HORIZONTAL_SCALE * Math.cos(angle),
          y: centerY + baseRadius * Math.sin(angle)
        };
      });
    } else {
      // Multi-level circles - distribute evenly
      const fieldsPerLevel = [];
      if (numLevels === 2) {
        fieldsPerLevel.push(Math.floor(fieldCount / 3));
        fieldsPerLevel.push(fieldCount - fieldsPerLevel[0]);
      } else {
        fieldsPerLevel.push(Math.floor(fieldCount / 4));
        fieldsPerLevel.push(Math.floor(fieldCount / 3));
        fieldsPerLevel.push(fieldCount - fieldsPerLevel[0] - fieldsPerLevel[1]);
      }
      
      let fieldIndex = 0;
      for (let level = 0; level < numLevels; level++) {
        const radius = baseRadius + (level * radiusIncrement);
        const fieldsInThisLevel = fieldsPerLevel[level];
        const angleOffset = level * (Math.PI / 6);
        
        for (let i = 0; i < fieldsInThisLevel; i++) {
          if (fieldIndex >= fieldCount) break;
          const angle = (i / fieldsInThisLevel) * 2 * Math.PI + angleOffset;
          positions[filteredFields[fieldIndex].id] = {
            x: centerX + radius * HORIZONTAL_SCALE * Math.cos(angle),
            y: centerY + radius * Math.sin(angle)
          };
          fieldIndex++;
        }
      }
    }
    
    // Users are NOT shown in default/filtered view - only when a node is selected
    
    return positions;
    };
    
    return getFilteredPositions(filteredNodes);
  }, [filteredNodes, searchQuery, filters.matchStatus, filters.overrideStatus]);

  // Calculate default positions (case in center, fields around it, users stacked left)
  const getDefaultPositions = () => {
    const centerX = 900 + STAGE_PADDING; // Increased to accommodate multi-level circles
    const centerY = 650 + STAGE_PADDING; // Increased to accommodate multi-level circles
    const positions = {};

    // Case goes in center
    nodeData.cases.forEach(caseNode => {
      positions[caseNode.id] = { x: centerX, y: centerY, focused: false, faded: false };
    });

    // Fields arrange in concentric circles around case with multiple levels for large field counts
    const fieldCount = nodeData.fields.length;
    const minArcSpace = 100; // Minimum space between fields on the circle
    
    // Determine number of levels needed based on field count
    let numLevels = 1;
    let fieldsPerLevel = [fieldCount];
    
    if (fieldCount > 20) {
      // For >20 fields, use 2 levels
      numLevels = 2;
      // Distribute fields: outer circle gets more (2/3), inner gets less (1/3)
      const innerCount = Math.ceil(fieldCount / 3);
      const outerCount = fieldCount - innerCount;
      fieldsPerLevel = [innerCount, outerCount];
    }
    
    if (fieldCount > 40) {
      // For >40 fields, use 3 levels
      numLevels = 3;
      const innerCount = Math.ceil(fieldCount / 4);
      const middleCount = Math.ceil(fieldCount / 3);
      const outerCount = fieldCount - innerCount - middleCount;
      fieldsPerLevel = [innerCount, middleCount, outerCount];
    }
    
    // Calculate radii for each level
    const baseRadius = 280;
    const radiusIncrement = 150; // Space between levels
    
    let fieldIndex = 0;
    for (let level = 0; level < numLevels; level++) {
      const radius = baseRadius + (level * radiusIncrement);
      const fieldsInThisLevel = fieldsPerLevel[level];
      
      for (let i = 0; i < fieldsInThisLevel; i++) {
        if (fieldIndex >= fieldCount) break;
        
        const field = nodeData.fields[fieldIndex];
        // Offset each level's starting angle slightly for visual variety
        const angleOffset = level * (Math.PI / numLevels);
        const angle = angleOffset + (i * 2 * Math.PI) / fieldsInThisLevel;
        const x = centerX + radius * HORIZONTAL_SCALE * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        positions[field.id] = { x, y, focused: false, faded: false };
        
        fieldIndex++;
      }
    }

    // Users are NOT shown in default view - only when a node is selected
    // No need to position users here

    return positions;
  };

  // Calculate focused positions when a node is selected
  useEffect(() => {
    if (!selectedNode) {
      setFocusedNodeData(getDefaultPositions());
      setRelatedNodeIds(new Set());
      return;
    }

    // Position focused view more to the left and higher up
    const centerX = 400 + STAGE_PADDING; // Moved left from 900
    const centerY = 350 + STAGE_PADDING; // Moved up from 650
    const newPositions = {};
    const newRelatedNodeIds = new Set();

    // If we have field history, create nodes from that data
    if (selectedNode.type === 'field' && fieldHistory.length > 0) {
      // Selected field goes to left side
      const selectedX = centerX - 250;
      newPositions[selectedNode.id] = { x: selectedX, y: centerY, focused: true, faded: false };

      // Create event nodes from history (newest on top)
      const eventSpacing = 140;
      const eventStartX = centerX - 50;
      const eventStartY = centerY - ((fieldHistory.length - 1) * eventSpacing) / 2;

      const userPositions = new Map();
      const newDynamicNodes = [];
      
      fieldHistory.forEach((entry, index) => {
        // Create an event node for this history entry
        const eventId = `event-${entry.id || index}`;
        const eventNode = {
          id: eventId,
          type: 'action',
          name: entry.activity_type === 'override' ? `Override: ${entry.override_type || 'value'}` :
                entry.activity_type === 'match_status_changed' ? 'Match Status Changed' :
                entry.activity_type === 'flag_changed' ? 'Flag Changed' : entry.activity_type,
          timestamp: new Date(entry.created_at).toLocaleString(),
          icon: entry.activity_type === 'override' ? '✏️' :
                entry.activity_type === 'match_status_changed' ? '✓' :
                entry.activity_type === 'flag_changed' ? '🚩' : '📝'
        };
        newDynamicNodes.push(eventNode);

        const x = eventStartX;
        const y = eventStartY + (index * eventSpacing);
        newPositions[eventId] = { x, y, focused: true, faded: false };
        newRelatedNodeIds.add(eventId);

        // Position the user who performed this action (if available)
        if (entry.created_by_username && !userPositions.has(entry.created_by_username)) {
          const userId = `user-${entry.created_by_username}`;
          const userNode = {
            id: userId,
            type: 'user',
            name: entry.created_by_username,
            actions: 1
          };
          newDynamicNodes.push(userNode);
          
          const userX = centerX + 150;
          const userY = y; // Same Y as the event
          newPositions[userId] = { x: userX, y: userY, focused: true, faded: false };
          newRelatedNodeIds.add(userId);
          userPositions.set(entry.created_by_username, { x: userX, y: userY, node: userNode });
        }
      });

      setDynamicNodes(newDynamicNodes);
      setRelatedNodeIds(newRelatedNodeIds);
    } else {
      // Clear dynamic nodes if not viewing field history
      setDynamicNodes([]);
      
      // Use the old mock data logic for non-field nodes or fields without history
    const relatedActions = nodeData.actions.filter(action => {
      if (selectedNode.type === 'field') {
        return action.relatedField === selectedNode.id;
      } else if (selectedNode.type === 'user') {
        return action.relatedUser === selectedNode.id;
      } else if (selectedNode.type === 'action') {
        return action.id === selectedNode.id;
      } else if (selectedNode.type === 'case') {
        return action.relatedField === null;
      }
      return false;
    });

    relatedActions.forEach(action => {
      newRelatedNodeIds.add(action.id);
      if (action.relatedField) newRelatedNodeIds.add(action.relatedField);
      if (action.relatedUser) newRelatedNodeIds.add(action.relatedUser);
    });
    setRelatedNodeIds(newRelatedNodeIds);

    // Selected node goes to left side
    const selectedX = centerX - 250;
    newPositions[selectedNode.id] = { x: selectedX, y: centerY, focused: true, faded: false };

    // Sort actions by timestamp (newest first)
    const sortedActions = relatedActions.sort((a, b) => {
      const getTimeValue = (timestamp) => {
        if (timestamp.includes('min ago')) return parseInt(timestamp.split(' ')[0]);
        if (timestamp.includes('hour ago')) return parseInt(timestamp.split(' ')[0]) * 60;
        if (timestamp.includes('day ago')) return parseInt(timestamp.split(' ')[0]) * 60 * 24;
          return 999;
      };
      
      const timeA = getTimeValue(a.timestamp);
      const timeB = getTimeValue(b.timestamp);
        return timeA - timeB;
      });

      // Actions arrange vertically to the right of selected node
      const actionStartX = centerX - 50;
      const actionSpacing = 140;
      const actionStartY = centerY - ((sortedActions.length - 1) * actionSpacing) / 2;
    
    sortedActions.forEach((action, index) => {
      const x = actionStartX;
      const y = actionStartY + (index * actionSpacing);
      newPositions[action.id] = { x, y, focused: true, faded: false };
    });

      // Users arrange to the right of actions
      const userStartX = centerX + 150;
      const userPositions = new Map();
      
    sortedActions.forEach((action, index) => {
      if (action.relatedUser && action.relatedField === selectedNode.id) {
        const user = nodeData.users.find(u => u.id === action.relatedUser);
        if (user && !userPositions.has(user.id)) {
          const x = userStartX;
            const y = actionStartY + (index * actionSpacing);
          newPositions[user.id] = { x, y, focused: true, faded: false };
          userPositions.set(user.id, { x, y });
          }
        }
      });
    }

    // Unrelated nodes move to edges and fade
    const allNodes = [...nodeData.fields, ...nodeData.users, ...nodeData.cases];
    allNodes.forEach(node => {
      if (!newRelatedNodeIds.has(node.id) && node.id !== selectedNode.id) {
        const angle = Math.random() * 2 * Math.PI;
        const newDistance = 400 + (Math.random() * 100);
        const x = centerX + newDistance * HORIZONTAL_SCALE * Math.cos(angle);
        const y = centerY + newDistance * Math.sin(angle);
        newPositions[node.id] = { x, y, focused: false, faded: true };
      }
    });

    setFocusedNodeData(newPositions);
    
    // Scroll to top-left when focusing on a node
    if (visualizationRef.current) {
      setTimeout(() => {
        const container = visualizationRef.current;
        container.scrollTo({
          left: 0,
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [selectedNode, nodeData, fieldHistory]);

  // Fetch field history when a field node is selected
  useEffect(() => {
    const fetchFieldHistory = async () => {
      if (!selectedNode || selectedNode.type !== 'field') {
        setFieldHistory([]);
        return;
      }

      setLoadingHistory(true);
      try {
        const encodedFieldName = encodeURIComponent(selectedNode.name);
        const url = `${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'}/api/activity/${loanId}/${encodedFieldName}`;
        
        console.log('Fetching field history:', url);
        
        const response = await fetch(url, {
          headers: window.Cypress ? {
            'X-Test-Mode': 'true',
            'X-Test-DB-Name': 'ocr_matching_test',
            'X-Test-User': 'gary'
          } : {}
        });

        console.log('Field history response:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('Field history data:', data);
          setFieldHistory(data || []);
        } else {
          console.error('Failed to fetch field history:', response.status, response.statusText);
          setFieldHistory([]);
        }
      } catch (error) {
        console.error('Error fetching field history:', error);
        setFieldHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchFieldHistory();
  }, [selectedNode, loanId]);

  const handleNodeClick = (node) => {
    if (selectedNode && selectedNode.id === node.id) {
      // Clicking the same node deselects it
      setSelectedNode(null);
      setDetailPanelOpen(false);
      setFieldHistory([]);
      setDynamicNodes([]);
      
      // Reset zoom to auto-fit and scroll to top
      setZoom('auto');
      if (visualizationRef.current) {
        setTimeout(() => {
          const container = visualizationRef.current;
          const centerX = (container.scrollWidth - container.clientWidth) / 2;
          container.scrollTo({
            left: Math.max(0, centerX),
            top: 0, // Top of content
            behavior: 'smooth'
          });
        }, 100);
      }
    } else {
      // Clicking a different node selects it
      setSelectedNode(node);
      setDetailPanelOpen(true);
    }
  };

  const getNodeIcon = (type, matchStatus) => {
    // For fields, show the appropriate match status icon
    if (type === 'field' && matchStatus) {
      switch (matchStatus) {
        case 'perfect':
        case 'matched':
          return <Check size={16} />;
        case 'maybe':
          return <AlertTriangle size={16} />;
        case 'mismatch':
          return <X size={16} />;
        case 'no_data':
          return <HelpCircle size={16} />;
        default:
          return <Target size={16} />; // Fallback to bullseye
      }
    }
    
    // For fields without match status, show bullseye
    if (type === 'field') {
      return <Target size={16} />;
    }
    
    switch (type) {
      case 'user': return <User size={16} />;
      case 'case': return <FileText size={16} />;
      case 'action': return <Activity size={16} />;
      default: return <Database size={16} />;
    }
  };

  const getNodeColor = (type) => {
    switch (type) {
      case 'field': return '#9ca3af'; // Gray for fields (they have match status colors)
      case 'user': return '#f59e0b';
      case 'case': return '#3b82f6'; // Blue for case (matches new center node color)
      case 'action': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const generateConnections = () => {
    const connections = [];
    
    // Get the set of visible node IDs
    const visibleNodeIds = new Set(filteredNodes.map(n => n.id));
    
    if (!selectedNode) {
      // Default view: only connect fields to case (users not shown)
      // Only create connections for visible nodes
      nodeData.fields.forEach(field => {
        // Skip if this field is filtered out
        if (!visibleNodeIds.has(field.id)) return;
        
        nodeData.cases.forEach(caseNode => {
          connections.push({
            id: `conn-${field.id}-${caseNode.id}`,
            from: field,
            to: caseNode,
            fromColor: getNodeColor('field'),
            toColor: getNodeColor('case')
          });
        });
      });
    } else if (selectedNode.type === 'field' && dynamicNodes.length > 0) {
      // Field history focus view: Field → Events → Users
      const eventNodes = dynamicNodes.filter(n => n.type === 'action');
      const userNodes = dynamicNodes.filter(n => n.type === 'user');

      // Connect field to each event
      eventNodes.forEach(event => {
          connections.push({
          id: `conn-${selectedNode.id}-${event.id}`,
          from: selectedNode,
          to: event,
          fromColor: getNodeColor('field'),
          toColor: getNodeColor('action')
          });
        });

      // Connect events to users (based on history data)
      fieldHistory.forEach((entry, index) => {
        const eventId = `event-${entry.id || index}`;
        const userId = `user-${entry.created_by_username}`;
        
        if (entry.created_by_username) {
          connections.push({
            id: `conn-${eventId}-${userId}`,
            from: { id: eventId },
            to: { id: userId },
            fromColor: getNodeColor('action'),
            toColor: getNodeColor('user')
          });
        }
      });
    } else {
      // Focus view: show action connections (mock data)
      const relatedActions = nodeData.actions.filter(action => {
        if (selectedNode.type === 'field') {
          return action.relatedField === selectedNode.id;
        } else if (selectedNode.type === 'user') {
          return action.relatedUser === selectedNode.id;
        } else if (selectedNode.type === 'case') {
          return true;
        }
        return false;
      });

      // Create left-to-right flow: Field → Actions → Users → Other Actions
      relatedActions.forEach(action => {
        // Connect field to action (left to right)
        if (action.relatedField === selectedNode.id) {
          connections.push({
            id: `conn-${selectedNode.id}-${action.id}`,
            from: selectedNode,
            to: action,
            fromColor: getNodeColor(selectedNode.type),
            toColor: getNodeColor('action')
          });
        }

        // Connect action to user (left to right)
        if (action.relatedUser) {
          const user = nodeData.users.find(u => u.id === action.relatedUser);
          if (user) {
            connections.push({
              id: `conn-${action.id}-${user.id}`,
              from: action,
              to: user,
              fromColor: getNodeColor('action'),
              toColor: getNodeColor('user')
            });
          }
        }
      });

      // Only connect users to their other actions if we're NOT focusing on a field
      if (selectedNode.type !== 'field') {
        // Connect users to their other actions (for context)
        const fieldUsers = [...new Set(relatedActions.map(action => action.relatedUser).filter(Boolean))];
        fieldUsers.forEach(userId => {
          const user = nodeData.users.find(u => u.id === userId);
          if (user) {
            const userOtherActions = nodeData.actions.filter(action => 
              action.relatedUser === userId && 
              action.relatedField !== selectedNode.id &&
              action.relatedField !== null // Exclude case-level actions
            );
            
            userOtherActions.forEach(otherAction => {
              connections.push({
                id: `conn-${userId}-${otherAction.id}`,
                from: user,
                to: otherAction,
                fromColor: getNodeColor('user'),
                toColor: getNodeColor('action')
              });
            });
          }
        });
      }
    }

    return connections;
  };

  const connections = useMemo(() => generateConnections(), [selectedNode, nodeData, dynamicNodes, fieldHistory, filteredNodes]);

  // Add 80px left padding for node positioning
  const horizontalOffset = 80;

  return (
    <HistoryContainer>
      <Header>
        <HeaderLeft>
          <HeaderTitle>
            <Activity size={24} />
            Case History - Node View
          </HeaderTitle>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <span>Loan ID: {loanId}</span>
            {(searchQuery || filters.matchStatus !== 'all' || filters.overrideStatus !== 'all') && (
              <span style={{ 
                background: '#3b82f6', 
                color: 'white', 
                padding: '2px 8px', 
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: '500'
              }}>
                {filteredNodeCount} {filteredNodeCount === 1 ? 'result' : 'results'}
              </span>
            )}
          </div>
        </HeaderLeft>
        
        <HeaderControls>
          <ViewToggle>
            <ViewButton 
              active={viewMode === 'nodes'} 
              onClick={() => setViewMode('nodes')}
            >
              <Grid size={16} />
              Nodes
            </ViewButton>
            <ViewButton 
              active={viewMode === 'timeline'} 
              onClick={() => setViewMode('timeline')}
            >
              <List size={16} />
              Timeline
            </ViewButton>
          </ViewToggle>
          
          {showBackButton && onBackClick && (
            <button
              data-cy="back-to-analysis-button"
              onClick={onBackClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginLeft: '1rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
            >
              <ArrowRight size={16} style={{ transform: 'rotate(180deg)' }} />
              Back to Analysis
            </button>
          )}
        </HeaderControls>
      </Header>

      <FilterSection>
        <FilterGroup>
          <FilterLabel>Type:</FilterLabel>
          <FilterSelect 
            value={filters.type} 
            onChange={(e) => setFilters({...filters, type: e.target.value})}
          >
            <option value="all">All Types</option>
            <option value="field">Fields</option>
            <option value="case">Case</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Time:</FilterLabel>
          <FilterSelect 
            value={filters.timeframe} 
            onChange={(e) => setFilters({...filters, timeframe: e.target.value})}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Match:</FilterLabel>
          <CustomSelect
            value={filters.matchStatus} 
            onChange={(value) => setFilters({...filters, matchStatus: value})}
            options={[
              { value: 'all', label: 'All Matches', icon: null },
              { value: 'perfect', label: 'Perfect Match', icon: <Check size={16} color="#22c55e" /> },
              { value: 'mismatch', label: 'Mismatch', icon: <X size={16} color="#ef4444" /> },
              { value: 'maybe', label: 'Maybe/Review', icon: <AlertTriangle size={16} color="#f59e0b" /> },
              { value: 'no_data', label: 'Not Found', icon: <HelpCircle size={16} color="#6b7280" /> },
            ]}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>Override:</FilterLabel>
          <CustomSelect
            value={filters.overrideStatus} 
            onChange={(value) => setFilters({...filters, overrideStatus: value})}
            options={[
              { value: 'all', label: 'All Status', icon: null },
              { value: 'user', label: 'User Override', icon: <User size={16} color="#8b5cf6" /> },
              { value: 'pending', label: 'Pending Review', icon: <Clock size={16} color="#f59e0b" /> },
              { value: 'system', label: 'System Match', icon: <Zap size={16} color="#10b981" /> },
            ]}
          />
        </FilterGroup>

        <div style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
          <SearchInput 
            ref={searchInputRef}
            type="text"
            placeholder="Search fields by name or value..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                color: '#6b7280',
                fontSize: '18px'
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>

        {(searchQuery || filters.matchStatus !== 'all' || filters.overrideStatus !== 'all') && (
          <button
            onClick={() => {
              setSearchQuery('');
              setFilters({
                ...filters,
                matchStatus: 'all',
                overrideStatus: 'all'
              });
            }}
            style={{
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              whiteSpace: 'nowrap'
            }}
            title="Clear all filters"
          >
            Clear Filters
          </button>
        )}
      </FilterSection>

      {/* Zoom Controls - Fixed position */}
      <ZoomControls detailPanelOpen={detailPanelOpen}>
        <ZoomButton data-cy="zoom-out-button" onClick={handleZoomOut} disabled={zoomLevel <= 0.2} title="Zoom Out">
          <ZoomOut size={16} />
        </ZoomButton>
        <ZoomLevel>{Math.round(zoomLevel * 100)}%</ZoomLevel>
        <ZoomButton data-cy="zoom-in-button" onClick={handleZoomIn} disabled={zoomLevel >= 3} title="Zoom In">
          <ZoomIn size={16} />
        </ZoomButton>
        <ZoomButton data-cy="zoom-reset-button" onClick={handleZoomReset} active={zoom === 'auto'} title="Auto Fit">
          <Maximize2 size={16} />
        </ZoomButton>
      </ZoomControls>

      <VisualizationArea ref={visualizationRef} data-cy="node-history-stage">
        <ScrollWrapper contentWidth={contentDimensions.width} contentHeight={contentDimensions.height} zoom={zoomLevel}>
          <ZoomableContent contentWidth={contentDimensions.width} contentHeight={contentDimensions.height} zoom={zoomLevel}>
            <NodesContainer offsetX={horizontalOffset}>
            {/* Render connections first (behind nodes) */}
            {(() => {
              // Calculate default positions once outside the loop
              const defaultPositions = getDefaultPositions();
              
              return connections.map(conn => {
                // Use the same position lookup logic as node rendering
                const getNodePos = (nodeId) => {
                  // Priority 1: Focused positions (when viewing field history)
                  if (selectedNode && focusedNodeData?.[nodeId]) {
                    return focusedNodeData[nodeId];
                  }
                  // Priority 2: Filtered positions (when filters are active)
                  if (filteredNodePositions?.[nodeId]) {
                    return { x: filteredNodePositions[nodeId].x, y: filteredNodePositions[nodeId].y };
                  }
                  // Priority 3: Default positions
                  return defaultPositions[nodeId] || { x: 900, y: 650 };
                };
                
                const fromPos = getNodePos(conn.from.id);
                const toPos = getNodePos(conn.to.id);
          
          const dx = toPos.x - fromPos.x;
          const dy = toPos.y - fromPos.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx) * 180 / Math.PI;
          
          const isFocused = selectedNode && (
            conn.from.id === selectedNode.id || 
            conn.to.id === selectedNode.id ||
            (focusedNodeData?.[conn.from.id]?.focused && focusedNodeData?.[conn.to.id]?.focused)
          );
          
          return (
            <ConnectionLine
              key={conn.id}
              data-connection-line="true"
              style={{
                left: fromPos.x,
                top: fromPos.y,
                width: distance,
                transform: `rotate(${angle}deg)`,
              }}
              fromColor={conn.fromColor}
              toColor={conn.toColor}
              focused={isFocused}
              faded={!isFocused}
            />
          );
        });
      })()}

        {/* Render nodes - use filtered nodes or focused nodes */}
        {(() => {
          // When a node is focused, show only related nodes
          if (selectedNode) {
            // Include both mock data nodes and dynamic nodes created from field history
            const allNodes = [...Object.values(nodeData).flat(), ...dynamicNodes];
            return allNodes.filter(node => relatedNodeIds.has(node.id) || node.id === selectedNode.id);
          }
          
          // Otherwise, use filtered nodes
          return filteredNodes;
        })()
        .map(node => {
          // Use filtered positions when filters are active, otherwise use focus or default
          let currentPos;
          
          if (selectedNode && focusedNodeData?.[node.id]) {
            // Use focus mode positions
            currentPos = focusedNodeData[node.id];
          } else if (filteredNodePositions && filteredNodePositions[node.id]) {
            // Use filtered layout positions (when filters are active)
            currentPos = {
              x: filteredNodePositions[node.id].x,
              y: filteredNodePositions[node.id].y,
              focused: false,
              faded: false
            };
          } else {
            // Use default positions (no filters active)
            const defaultPositions = getDefaultPositions();
            currentPos = defaultPositions[node.id] || { 
              x: 900, 
              y: 650, 
              focused: false, 
              faded: false 
            };
          }
          
          return (
            <NodeContainer 
              key={node.id} 
              top={currentPos.y} 
              left={currentPos.x}
              zIndex={currentPos.focused ? 10 : 1}
            >
              <Node 
                type={node.type}
                matchStatus={node.matchStatus}
                focused={currentPos.focused}
                faded={currentPos.faded}
                className={selectedNode && selectedNode.id === node.id ? 'selected' : ''}
                data-match-status={node.matchStatus}
                data-node-type={node.type}
                onClick={() => handleNodeClick(node)}
              >
                
                <NodeIcon type={node.type} matchStatus={node.matchStatus} focused={currentPos.focused}>
                  {getNodeIcon(node.type, node.matchStatus)}
                </NodeIcon>
                <NodeTitle nodeType={node.type} matchStatus={node.matchStatus} focused={currentPos.focused}>{node.name}</NodeTitle>
                <NodeSubtitle nodeType={node.type} matchStatus={node.matchStatus} focused={currentPos.focused}>
                  {node.type === 'field' && `${node.changes} changes`}
                  {node.type === 'user' && `${node.actions} actions`}
                  {node.type === 'action' && node.timestamp}
                  {node.type === 'case' && node.status}
                </NodeSubtitle>
              </Node>
            </NodeContainer>
          );
            })}
            </NodesContainer>
          </ZoomableContent>
        </ScrollWrapper>
      </VisualizationArea>

      <DetailPanel data-cy="detail-panel" isOpen={detailPanelOpen}>
        {selectedNode && (
          <>
            <DetailHeader>
              <div style={{ flex: 1 }}>
                <DetailTitle>{selectedNode.name}</DetailTitle>
                <button
                  onClick={() => {
                    setSelectedNode(null);
                    setDetailPanelOpen(false);
                    setFieldHistory([]);
                    setDynamicNodes([]);
                    setZoom('auto');
                  }}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: '#e5e7eb',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#d1d5db';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#e5e7eb';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                >
                  <X size={16} />
                  Close Zoom View
                </button>
              </div>
              <CloseButton onClick={() => setDetailPanelOpen(false)}>
                <EyeOff size={20} />
              </CloseButton>
            </DetailHeader>

            <DetailContent>
              <DetailSection>
                <DetailSectionTitle>Node Details</DetailSectionTitle>
                <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                  <div><strong>Type:</strong> {selectedNode.type}</div>
                  <div><strong>ID:</strong> {selectedNode.id}</div>
                  {selectedNode.value && <div><strong>Current Value:</strong> {selectedNode.value}</div>}
                  {selectedNode.changes && <div><strong>Changes:</strong> {selectedNode.changes}</div>}
                  {selectedNode.actions && <div><strong>Actions:</strong> {selectedNode.actions}</div>}
                  {selectedNode.status && <div><strong>Status:</strong> {selectedNode.status}</div>}
                </div>
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Field History</DetailSectionTitle>
                {loadingHistory ? (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', padding: '1rem 0' }}>
                    Loading history...
                  </div>
                ) : fieldHistory.length === 0 ? (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', padding: '1rem 0' }}>
                    No history available for this field.
                  </div>
                ) : (
                  fieldHistory.map((entry, i) => (
                  <TimelineItem key={i} data-history-entry="true">
                      <TimelineIcon color={
                        (entry.activity_type === 'override' || entry.activity_type === 'field_override') ? '#3b82f6' :
                        (entry.activity_type === 'match_status_changed' || entry.activity_type === 'match_status_change') ? '#10b981' :
                        (entry.activity_type === 'flag_changed' || entry.activity_type === 'flag_set') ? '#f59e0b' : '#6b7280'
                      }>
                        {(entry.activity_type === 'override' || entry.activity_type === 'field_override') ? <Edit size={12} /> :
                         (entry.activity_type === 'match_status_changed' || entry.activity_type === 'match_status_change') ? <CheckCircle size={12} /> :
                         (entry.activity_type === 'flag_changed' || entry.activity_type === 'flag_set') ? <AlertTriangle size={12} /> :
                         <Activity size={12} />}
                    </TimelineIcon>
                    <TimelineContent>
                      <TimelineTitle>
                          {(entry.activity_type === 'override' || entry.activity_type === 'field_override') && 'Override'}
                          {(entry.activity_type === 'match_status_changed' || entry.activity_type === 'match_status_change') && 'Match Status Changed'}
                          {(entry.activity_type === 'flag_changed' || entry.activity_type === 'flag_set') && 'Flag Changed'}
                          {!['override', 'field_override', 'match_status_changed', 'match_status_change', 'flag_changed', 'flag_set'].includes(entry.activity_type) && entry.activity_type.replace(/_/g, ' ')}
                      </TimelineTitle>
                        {entry.old_value && entry.new_value && (
                          <div style={{ fontSize: '0.75rem', color: '#4b5563', margin: '0.25rem 0' }}>
                            <span style={{ color: '#ef4444' }}>{entry.old_value}</span>
                            {' → '}
                            <span style={{ color: '#22c55e' }}>{entry.new_value}</span>
                          </div>
                        )}
                        {entry.comment && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', margin: '0.25rem 0' }}>
                            "{entry.comment}"
                          </div>
                        )}
                      <TimelineMeta>
                        <Clock size={12} />
                          {new Date(entry.created_at).toLocaleDateString()} {new Date(entry.created_at).toLocaleTimeString()}
                          {entry.created_by_username && (
                            <>
                              <User size={12} />
                              {entry.created_by_username}
                            </>
                          )}
                      </TimelineMeta>
                    </TimelineContent>
                  </TimelineItem>
                  ))
                )}
              </DetailSection>

              <DetailSection>
                <DetailSectionTitle>Connected Nodes</DetailSectionTitle>
                <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                  {connections
                    .filter(conn => conn.from.id === selectedNode.id || conn.to.id === selectedNode.id)
                    .map(conn => (
                      <div key={conn.id} style={{ marginBottom: '0.5rem' }}>
                        {conn.from.id === selectedNode.id ? (
                          <div>→ Connected to: <strong>{conn.to.name}</strong></div>
                        ) : (
                          <div>← Connected from: <strong>{conn.from.name}</strong></div>
                        )}
                      </div>
                    ))}
                </div>
              </DetailSection>
            </DetailContent>
          </>
        )}
      </DetailPanel>
    </HistoryContainer>
  );
};

export default NodeBasedHistory;
